import React from 'react';
import { Link } from 'react-router-dom';
import { User, Lightbulb, Heart, TrendingUp, Phone, Mail } from 'lucide-react';
import { AnimateOnScroll } from '../components/AnimateOnScroll';
import { PhilosophieSection } from '../components/PhilosophieSection';
import { Button } from '../components/ui/button';

const BOOKING_URL = "https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled";

export const About = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[600px] -right-20 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />

      {/* Hero */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50 relative">
        <div className="mx-auto max-w-4xl text-center relative z-10">
          <AnimateOnScroll>
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Qui suis-je
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              À propos
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed">
              Un consultant informatique indépendant qui met son expertise — et sa passion — 
              au service des TPE et PME de la région lyonnaise.
            </p>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Main content */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-4xl">
          <div className="prose prose-lg max-w-none">
            {/* Présentation */}
            <AnimateOnScroll>
              <div className="mb-10 sm:mb-12">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0">Qui suis-je ?</h2>
                </div>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  Je suis Richard SAAD, consultant informatique indépendant basé dans la région lyonnaise. 
                  Je me suis spécialisé dans l'accompagnement des TPE et PME sur leurs enjeux systèmes, 
                  réseaux, sécurité et cloud — avec une vraie passion pour faire les choses bien.
                </p>
              </div>
            </AnimateOnScroll>

            {/* Expérience */}
            <AnimateOnScroll delay={100}>
              <div className="mb-10 sm:mb-12">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center shadow-lg flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0">Mon expérience</h2>
                </div>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  J'ai accompagné au fil des années des environnements variés : <strong className="text-orange-600">hôtellerie, 
                  restauration rapide, service public, industrie</strong>... avec un point commun : 
                  des structures qui ont besoin d'une informatique fiable, mais qui n'ont pas forcément 
                  une équipe IT interne dédiée.
                </p>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mt-4">
                  Cette diversité m'a permis de développer une capacité d'adaptation rapide et une 
                  compréhension fine des enjeux spécifiques à chaque secteur d'activité.
                </p>
              </div>
            </AnimateOnScroll>

            {/* Manière de travailler */}
            <AnimateOnScroll delay={150}>
              <div className="mb-10 sm:mb-12">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-lg flex-shrink-0">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0">Ma manière de travailler</h2>
                </div>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  Mon approche est simple : <strong>comprendre votre contexte, parler un langage clair, 
                  proposer des solutions adaptées et rester présent dans la durée</strong>.
                </p>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mt-4">
                  Chaque client est suivi individuellement, sans standardisation forcée, pour que votre 
                  informatique colle vraiment à votre manière de travailler. Je prends le temps d'expliquer, 
                  de vulgariser, pour que vous compreniez les enjeux et que nous prenions ensemble les bonnes décisions.
                </p>
              </div>
            </AnimateOnScroll>

            {/* Valeurs */}
            <AnimateOnScroll delay={200}>
              <div className="mb-10 sm:mb-12">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg flex-shrink-0">
                    <Lightbulb className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0">Mes valeurs</h2>
                </div>
                <ul className="space-y-3 text-base sm:text-lg text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 font-bold">•</span>
                    <span><strong className="text-gray-900">Proximité</strong> : une relation humaine et un suivi personnalisé</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 font-bold">•</span>
                    <span><strong className="text-gray-900">Transparence</strong> : vous savez toujours où vous en êtes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 font-bold">•</span>
                    <span><strong className="text-gray-900">Pragmatisme</strong> : des solutions réalistes adaptées à votre contexte</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 font-bold">•</span>
                    <span><strong className="text-gray-900">Engagement</strong> : je suis là pour la durée, pas juste pour un projet ponctuel</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 font-bold">•</span>
                    <span><strong className="text-gray-900">Passion</strong> : continuer à grandir sans jamais se dénaturer</span>
                  </li>
                </ul>
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* Section Philosophie - variante allégée */}
      <PhilosophieSection variant="compact" />

      {/* CTA */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50 relative z-10">
        <div className="mx-auto max-w-3xl text-center">
          <AnimateOnScroll>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Envie d'en discuter ?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-8">
              Je serais ravi d'échanger avec vous sur vos besoins informatiques.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Phone className="w-5 h-5 mr-2" />
                  Prendre rendez-vous (30 min)
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                <Link to="/contact">
                  <Mail className="w-5 h-5 mr-2" />
                  Me contacter
                </Link>
              </Button>
            </div>
          </AnimateOnScroll>
        </div>
      </section>
    </div>
  );
};
